/**
 * Users domain API functions (Step 7.1.15).
 *
 * List/get org users (admin only).
 * Extracted from adminData/index.js for team scalability.
 */
import { API_BASE_URL, getApiHeaders } from "./core";

/** GET /api/admin/users — org users (admin only). */
export function fetchAdminUsers() {
  return fetch(`${API_BASE_URL}/api/admin/users`, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) throw new Error("Benutzer konnten nicht geladen werden.");
    return res.json();
  });
}

/** GET /api/admin/users/:id — single org user (admin only). */
export function fetchAdminUser(userId) {
  return fetch(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}`, {
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error("Benutzer konnte nicht geladen werden.");
    }
    return res.json();
  });
}

/**
 * GET /api/admin/users/picker-options — PICK1c assignee picker.
 *
 * The searchable, paginated counterpart to ``fetchAdminUsers`` above,
 * which takes NO parameters and returns the whole organisation. Active
 * users only; a deactivated one is still nameable through
 * ``fetchAdminUserPickerOptionById``.
 *
 * Unlike the admin-only list route this is open to managers too, which
 * is what stops their assignee field from being silently empty.
 */
export function fetchAdminUserPickerOptions({ q, limit, offset } = {}) {
  const sp = new URLSearchParams();
  if (q != null && String(q).trim()) sp.set("q", String(q).trim());
  if (limit != null) sp.set("limit", String(limit));
  if (offset != null) sp.set("offset", String(offset));
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/users/picker-options${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error("Benutzer konnten nicht geladen werden.");
    const data = await res.json();
    if (!data || !Array.isArray(data.items)) {
      throw new Error(
        'Ungültige Antwort von GET /api/admin/users/picker-options: erwartet paginierte Antwort mit "items".'
      );
    }
    return {
      items: data.items,
      total: typeof data.total === "number" ? data.total : data.items.length,
    };
  });
}

/**
 * GET /api/admin/users/{id}/picker-option — name ONE user, active or not.
 *
 * Returns ``null`` on 404 so a deleted or invisible assignee leaves the
 * trigger on its placeholder instead of breaking the form. Every other
 * failure throws: a network error or a 403 must not look like "this
 * person does not exist".
 */
export function fetchAdminUserPickerOptionById(userId) {
  return fetch(
    `${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/picker-option`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Benutzer konnte nicht geladen werden.");
    return res.json();
  });
}
